import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveWidgetLang } from '../../lib/widget-translations';
import type { PresetFormValues } from '../../lib/types';
import { createWidgetPreviewConfig } from './lib/widget-preview-config';
import { PreviewControls } from './preview-controls';
import type { PreviewControlState } from './types';
import { WidgetPreview } from './widget-preview';
import styles from './widget-preview.module.scss';

interface PresetWidgetPreviewProps {
  settings: PresetFormValues;
}

const INITIAL_CONTROLS: PreviewControlState = {
  device: 'desktop',
  displayState: 'open',
  showContactForm: true,
  showTyping: false,
  showFormError: false,
};

export const PresetWidgetPreview = ({ settings }: PresetWidgetPreviewProps) => {
  const { i18n } = useTranslation();
  const [controls, setControls] = useState<PreviewControlState>(INITIAL_CONTROLS);
  const [resetSignal, setResetSignal] = useState(0);
  const [clearSignal, setClearSignal] = useState(0);
  const widgetLang = resolveWidgetLang(i18n.resolvedLanguage ?? i18n.language);
  const config = createWidgetPreviewConfig(settings, widgetLang);

  const resetDemo = () => {
    setControls(INITIAL_CONTROLS);
    setResetSignal((value) => value + 1);
  };

  return (
    <div className={styles.previewLayout}>
      <PreviewControls
        value={controls}
        onChange={setControls}
        onReset={resetDemo}
        onClear={() => {
          setControls((value) => ({
            ...value,
            showContactForm: false,
            showTyping: false,
            showFormError: false,
          }));
          setClearSignal((value) => value + 1);
        }}
      />
      <div
        className={`${styles.siteCanvas} ${controls.device === 'mobile' ? styles.mobileCanvas : ''}`}
      >
        <div className={styles.mockBrowserBar}>
          <i />
          <i />
          <i />
          <span>example.com</span>
        </div>
        <div className={styles.mockSiteContent}>
          <div className={styles.mockNav} />
          <div className={styles.mockHero}>
            <div>
              <span />
              <strong />
              <strong />
              <small />
              <button type="button" tabIndex={-1} aria-hidden="true" />
            </div>
            <aside />
          </div>
          <div className={styles.mockCards}>
            <span />
            <span />
            <span />
          </div>
        </div>
        <WidgetPreview
          config={config}
          device={controls.device}
          displayState={controls.displayState}
          showContactForm={controls.showContactForm}
          showTyping={controls.showTyping}
          showFormError={controls.showFormError}
          resetSignal={resetSignal}
          clearSignal={clearSignal}
          onDisplayStateChange={(displayState) =>
            setControls((value) => ({ ...value, displayState }))
          }
          onContactFormVisibilityChange={(showContactForm) =>
            setControls((value) => ({ ...value, showContactForm }))
          }
        />
      </div>
    </div>
  );
};
